/**
 * The staff page for drawing a project's boundary over its orthophoto: toolbar, map, and a side
 * panel with the name, the checks and the save (boundary-editor design §4). With `viewObjectId`
 * it shows that boundary read-only instead, with a way to start a new one from it.
 */
import { useCallback, useDeferredValue, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { FeatureCollection, Position } from 'geojson';
import { Alert, Box, Button, Card, Center, FileInput, Flex, Group, List, Loader, Modal, ScrollArea, Select, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueries } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { errorMessage } from '@/api/errors';
import { useCreateDrawnBoundary, useProjectObjects, type DataObject } from '@/api/objects';
import { useProject } from '@/api/projects';
import { projectViewerLink, projectViewerToken, type GrantedObject, type ViewerToken } from '@/api/viewer';
import { EmptyState } from '@/components/EmptyState';
import { FullBleed } from '@/components/FullBleed';
import { StaffBreadcrumbs } from '@/components/StaffBreadcrumbs';
import { useFormat } from '@/components/useFormat';
import { usePageTitle } from '@/components/usePageTitle';
import { useReportViewer } from '@/features/reports/useReportViewer';
import { rasterTileTemplate, type LayerRef } from '@/map/layers';
import { tileCacheRequest } from '@/map/tileCache';
import { useRasterMeta } from '@/map/useRasterMeta';
import { loadLinkedGpkgLayer } from '@/map/vectorSource';
import { brand } from '@/theme/theme';
import { BoundaryMap } from './BoundaryMap';
import { initialState, reducer } from './boundaryEditor';
import { areaHa, polygonsFromGeoJson, toMultiPolygon } from './geometry';
import { importPolygons } from './importPolygons';
import { validate } from './validate';

type Bounds = [number, number, number, number];

const CZECH_REPUBLIC: Bounds = [12.09, 48.55, 18.86, 51.06];
const NO_ORTHO = 'none';

function isTyping(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
}

/** Every coordinate of a collection's geometries, however deeply nested. */
function positions(node: unknown, out: Position[] = []): Position[] {
  if (Array.isArray(node)) {
    if (typeof node[0] === 'number') out.push(node as Position);
    else for (const child of node) positions(child, out);
  }
  return out;
}

function bbox(fc: FeatureCollection): Bounds | null {
  const ps = fc.features.flatMap((f) => (f.geometry && 'coordinates' in f.geometry ? positions(f.geometry.coordinates) : []));
  if (!ps.length) return null;
  const xs = ps.map((p) => p[0]);
  const ys = ps.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

function notifyFailure(message: string | null) {
  if (message) notifications.show({ color: 'red', message });
}

const ready = (objects: DataObject[] | undefined, kind: DataObject['kind']) => (objects ?? []).filter((o) => o.kind === kind && o.state === 'ready');

export interface BoundaryEditorPageProps {
  projectId: string;
  /** Show this boundary read-only instead of the editor. */
  viewObjectId?: string;
  /** Start the drawing from this boundary, as if chosen in "start from". */
  from?: string;
}

export function BoundaryEditorPage({ projectId, viewObjectId, from }: BoundaryEditorPageProps) {
  const { t } = useTranslation('boundary');
  const readOnly = viewObjectId !== undefined;
  const { date, number } = useFormat();
  const navigate = useNavigate();
  const project = useProject(projectId);
  const objects = useProjectObjects(projectId);
  const create = useCreateDrawnBoundary(projectId);

  const orthos = useMemo(() => ready(objects.data, 'orthophoto').sort((a, b) => b.created_at.localeCompare(a.created_at)), [objects.data]);
  const boundaries = useMemo(() => ready(objects.data, 'boundary'), [objects.data]);
  const viewed = readOnly ? boundaries.find((b) => b.public_id === viewObjectId) : undefined;
  const title = readOnly ? viewed?.name || t('view.title') : t('title');
  usePageTitle(title);

  const ids = [...orthos, ...boundaries].map((o) => o.public_id).join(',');
  // The ids each credential (or failure) answers for: a source replaced by a newer list can still
  // deliver late, and its answer says nothing about objects it was not asked for.
  const [answeredFor] = useState(() => new WeakMap<ViewerToken, string>());
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const fetchToken = useCallback(
    // With nothing to draw there is no token to ask for; the source simply never resolves.
    (): Promise<ViewerToken> =>
      ids
        ? projectViewerToken(projectId, ids.split(',')).then(
            (token) => {
              answeredFor.set(token, ids);
              return token;
            },
            (e: unknown) => {
              setFailedFor(ids);
              throw e;
            },
          )
        : new Promise<ViewerToken>(() => undefined),
    [projectId, ids, answeredFor],
  );
  const fetchLink = useCallback((object: string, path: string) => projectViewerLink(projectId, object, path), [projectId]);
  const { source, linkSource, credential, error: viewerError } = useReportViewer(fetchToken, fetchLink);
  const granted = useMemo(() => new Map<string, GrantedObject>((credential?.objects ?? []).map((g) => [g.object, g])), [credential]);

  const [orthoChoice, setOrthoChoice] = useState<string | undefined>(undefined);
  const orthoId = orthoChoice ?? orthos[0]?.public_id ?? NO_ORTHO;
  const orthoRef = useMemo((): LayerRef | null => {
    const g = granted.get(orthoId);
    return g && g.via === 'tiler' ? { object: g.object, object_kind: 'orthophoto', via: 'tiler', path: g.path, digest: g.digest } : null;
  }, [granted, orthoId]);
  const orthoTiles = credential && orthoRef ? rasterTileTemplate(credential.tiler_base_url, orthoRef) : null;
  const transformTiles = useMemo(() => tileCacheRequest(source), [source]);
  const rasterRefs = useMemo(() => (orthoRef ? [orthoRef] : []), [orthoRef]);
  const meta = useRasterMeta(rasterRefs, source);

  const linkBoundaries = useMemo(() => boundaries.filter((b) => granted.get(b.public_id)?.via === 'link'), [boundaries, granted]);
  const outlineBoundaries = useMemo(() => linkBoundaries.filter((b) => b.public_id !== viewObjectId), [linkBoundaries, viewObjectId]);
  const outlineLayers = useQueries({
    queries: outlineBoundaries.map((b) => {
      const g = granted.get(b.public_id)!;
      return {
        queryKey: ['boundary-outline', g.object, g.path, g.digest],
        queryFn: () => loadLinkedGpkgLayer({ object: g.object, path: g.path, digest: g.digest }, linkSource),
        staleTime: Infinity,
        retry: false,
      };
    }),
    combine: (results) => ({ layers: results.map((r) => r.data), pending: results.some((r) => r.isPending) }),
  });
  const outlines = useMemo(
    (): FeatureCollection => ({ type: 'FeatureCollection', features: outlineLayers.layers.flatMap((layer) => layer?.features ?? []) }),
    [outlineLayers.layers],
  );
  const orthoBounds = orthoRef ? meta.metas[orthoRef.object]?.bounds : undefined;
  const outlineBounds = useMemo(() => bbox(outlines), [outlines]);
  // The map fits only once, so it waits until everything it could fit to has answered.
  const grantsSettled = !ids || credential !== null || viewerError != null;
  const orthoSettled = orthoId === NO_ORTHO || (grantsSettled && (!orthoRef || orthoRef.object in meta.metas || orthoRef.object in meta.errors));

  const [state, dispatch] = useReducer(reducer, undefined, () => initialState());

  /** Loads a project boundary's polygons into the drawing; resolves to why it could not, or null. */
  const startFrom = useCallback(
    async (objectId: string): Promise<string | null> => {
      const g = boundaries.some((b) => b.public_id === objectId) ? granted.get(objectId) : undefined;
      if (!g || g.via !== 'link') return t('view.unavailable');
      try {
        const polygons = polygonsFromGeoJson(await loadLinkedGpkgLayer({ object: g.object, path: g.path, digest: g.digest }, linkSource));
        if (!polygons.length) return t('import.no_polygon');
        dispatch({ type: 'replace', polygons, source: g.object });
        return null;
      } catch (e) {
        return errorMessage(e);
      }
    },
    [boundaries, granted, linkSource, t],
  );

  // The viewed boundary, or the one `from` names, is loaded once, as soon as its grant can be known.
  const seedId = viewObjectId ?? from ?? null;
  const [seedResult, setSeedResult] = useState<'loaded' | 'failed' | null>(null);
  const seedStarted = useRef(false);
  const seedIsBoundary = seedId !== null && boundaries.some((b) => b.public_id === seedId);
  const grantsCurrent = !ids || (credential !== null && answeredFor.get(credential) === ids) || failedFor === ids;
  // Start (and so settle) only on an answer that can be trusted for the seed: its grant, a token
  // asked for the current objects, or an object list that, fully fetched, does not hold it.
  const seedAnswerable =
    seedId !== null && !objects.isPending && (seedIsBoundary ? granted.has(seedId) || grantsCurrent : !objects.isFetching);
  useEffect(() => {
    if (!seedId || seedStarted.current || !seedAnswerable) return;
    seedStarted.current = true;
    void startFrom(seedId).then((failure) => {
      setSeedResult(failure ? 'failed' : 'loaded');
      if (!readOnly) notifyFailure(failure);
    });
  }, [seedId, seedAnswerable, startFrom, readOnly]);
  const seeding = seedId !== null && seedResult === null;

  const settled = !objects.isPending && grantsSettled && orthoSettled && !outlineLayers.pending && !seeding;
  const initialBounds = settled ? (orthoBounds ?? outlineBounds ?? CZECH_REPUBLIC) : null;

  // validate() is quadratic: it runs on the deferred polygons so dragging stays smooth.
  const deferredPolygons = useDeferredValue(state.polygons);
  const problems = useMemo(() => validate(deferredPolygons), [deferredPolygons]);
  const [basemap, setBasemap] = useState<'osm' | 'ortho'>('osm');

  const drafting = state.draft.length > 0;
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      if (e.key === 'Enter') {
        // A focused toolbar button would otherwise also take the Enter as a click.
        if (drafting) e.preventDefault();
        dispatch({ type: 'close' });
      } else if (e.key === 'Escape') {
        dispatch({ type: 'cancel' });
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        dispatch({ type: 'undo' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drafting, readOnly]);

  const [name, setName] = useState(() => t('defaultName', { date: date(new Date().toISOString()) }));
  const [notes, setNotes] = useState('');
  const [saveError, setSaveError] = useState<{ message: string; detail: string } | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  if (project.isPending) {
    return (
      <Center py="xl">
        <Loader color="forest" />
      </Center>
    );
  }
  if (project.isError) return <EmptyState title={errorMessage(project.error)} />;
  const p = project.data;
  const closed = (p.state ?? 'active') !== 'active';
  // Until the deferred checks catch up with the polygons, `problems` describes older ones.
  const checked = deferredPolygons === state.polygons;
  const canSave = !closed && checked && state.polygons.length > 0 && problems.length === 0 && name.trim() !== '' && !create.isPending;

  const save = async () => {
    setSaveError(null);
    try {
      await create.mutateAsync({ name: name.trim(), notes, geometry: toMultiPolygon(state.polygons), copied_from: state.source });
      notifications.show({ color: 'forest', message: t('saved') });
      void navigate({ to: '/staff/projects/$projectId', params: { projectId } });
    } catch (e) {
      const withDetail = e instanceof ApiError && (e.code === 'boundary.invalid_geometry' || e.code === 'boundary.bad_geometry');
      setSaveError({ message: errorMessage(e), detail: withDetail ? e.message : '' });
    }
  };

  const selectedShell = state.selected === null ? null : { polygon: state.selected, hole: -1 };
  const help = state.mode === 'draw' || state.mode === 'hole' ? t('help.draw') : state.mode === 'edit' ? t('help.edit') : null;

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <StaffBreadcrumbs project={p} current={title} />
        <Title order={1}>{title}</Title>
      </Stack>
      {closed && !readOnly && <Alert color="yellow">{t('closed')}</Alert>}

      <FullBleed>
        <Flex gap="md" direction={{ base: 'column', md: 'row' }} align="stretch">
          <Stack gap="xs" style={{ flex: 1 }}>
            {readOnly && seedResult === 'failed' && <Alert color="red">{t('view.unavailable')}</Alert>}
            {!readOnly && (
              <>
                <Group gap="xs">
                  <Button variant={state.mode === 'draw' ? 'filled' : 'default'} onClick={() => dispatch({ type: 'mode', mode: 'draw' })}>
                    {t('tools.draw')}
                  </Button>
                  <Button variant={state.mode === 'hole' ? 'filled' : 'default'} disabled={state.selected === null} onClick={() => dispatch({ type: 'mode', mode: 'hole' })}>
                    {t('tools.hole')}
                  </Button>
                  <Button variant={state.mode === 'edit' ? 'filled' : 'default'} onClick={() => dispatch({ type: 'mode', mode: 'edit' })}>
                    {t('tools.edit')}
                  </Button>
                  <Button variant="default" disabled={selectedShell === null} onClick={() => selectedShell && dispatch({ type: 'deleteRing', ring: selectedShell })}>
                    {t('tools.deletePolygon')}
                  </Button>
                  <Button variant="default" disabled={state.history.length === 0} onClick={() => dispatch({ type: 'undo' })}>
                    {t('tools.undo')}
                  </Button>
                </Group>
                {help && (
                  <Text fz={13} c={brand.textMuted}>
                    {help}
                  </Text>
                )}
              </>
            )}
            <Box style={{ flex: 1, minHeight: 480, height: 'calc(100vh - 300px)' }}>
              <BoundaryMap
                state={state}
                dispatch={dispatch}
                basemap={basemap}
                onBasemapChange={setBasemap}
                orthoTiles={orthoTiles}
                transformRequest={transformTiles}
                outlines={outlines}
                problems={problems}
                initialBounds={initialBounds}
                readOnly={readOnly}
              />
            </Box>
          </Stack>

          <Card w={{ base: '100%', md: 360 }} p="md">
            <ScrollArea h={{ base: 'auto', md: 'calc(100vh - 300px)' }} type="auto" offsetScrollbars>
              <Stack gap="md">
                <Select
                  label={t('ortho')}
                  value={orthoId}
                  onChange={(v) => setOrthoChoice(v ?? NO_ORTHO)}
                  allowDeselect={false}
                  data={[...orthos.map((o) => ({ value: o.public_id, label: o.name })), { value: NO_ORTHO, label: t('noOrtho') }]}
                />
                {!readOnly && (
                  <>
                    <Group gap="xs" align="flex-end" wrap="nowrap">
                      <Select
                        style={{ flex: 1 }}
                        label={t('startFrom')}
                        value={state.source}
                        onChange={(v) => v && void startFrom(v).then(notifyFailure)}
                        allowDeselect={false}
                        data={linkBoundaries.map((b) => ({ value: b.public_id, label: b.name }))}
                        disabled={linkBoundaries.length === 0}
                      />
                      <Button variant="default" onClick={() => setImportOpen(true)}>
                        {t('import.open')}
                      </Button>
                    </Group>
                    <TextInput label={t('name')} required maxLength={200} value={name} onChange={(e) => setName(e.currentTarget.value)} />
                    <Textarea label={t('notes')} maxLength={500} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} />
                  </>
                )}
                <Text fz={14}>{t('stats', { count: state.polygons.length, area: number(areaHa(state.polygons), 2) })}</Text>
                {readOnly ? (
                  <Button disabled={closed || seedResult !== 'loaded'} onClick={() => void navigate({ to: '/staff/projects/$projectId/boundary', params: { projectId }, search: { from: viewObjectId } })}>
                    {t('view.edit')}
                  </Button>
                ) : (
                  <>
                    {problems.length > 0 && (
                      <List fz={14} c="red">
                        {problems.map((pr, i) => (
                          <List.Item key={`${pr.code}:${pr.polygon ?? ''}:${i}`}>{t(`problems.${pr.code}`, { n: (pr.polygon ?? 0) + 1 })}</List.Item>
                        ))}
                      </List>
                    )}
                    {saveError && (
                      <Alert color="red">
                        <Text fz={14}>{saveError.message}</Text>
                        {saveError.detail && <Text fz={13}>{saveError.detail}</Text>}
                      </Alert>
                    )}
                    <Button disabled={!canSave} loading={create.isPending} onClick={() => void save()}>
                      {t('save')}
                    </Button>
                  </>
                )}
              </Stack>
            </ScrollArea>
          </Card>
        </Flex>
      </FullBleed>

      {!readOnly && (
        <ImportModal
          opened={importOpen}
          onClose={() => setImportOpen(false)}
          onImport={(polygons) => {
            dispatch({ type: 'replace', polygons });
            setImportOpen(false);
          }}
        />
      )}
    </Stack>
  );
}

function ImportModal({ opened, onClose, onImport }: { opened: boolean; onClose: () => void; onImport: (polygons: ReturnType<typeof polygonsFromGeoJson>) => void }) {
  const { t } = useTranslation('boundary');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const apply = () => {
    const result = importPolygons(text);
    if (!result.ok) {
      setError(t(`import.${result.reason}`));
      return;
    }
    setText('');
    setError(null);
    onImport(result.polygons);
  };
  return (
    <Modal opened={opened} onClose={onClose} title={t('import.title')} size="lg">
      <Stack gap="sm">
        <Textarea
          label={t('import.paste')}
          value={text}
          onChange={(e) => {
            setText(e.currentTarget.value);
            setError(null);
          }}
          autosize
          minRows={6}
          maxRows={14}
        />
        <FileInput
          label={t('import.file')}
          accept=".geojson,.json,.kml"
          onChange={(file) => {
            setError(null);
            if (file) void file.text().then(setText, () => setError(t('import.invalid')));
          }}
        />
        {error && (
          <Text c="red" fz={14} role="alert">
            {error}
          </Text>
        )}
        <Group justify="flex-end">
          <Button onClick={apply} disabled={!text.trim()}>
            {t('import.apply')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
